(() => {
  const gsap = window.gsap;
  if (!gsap || !gsap.matchMedia) return;

  const mounts = new WeakMap();
  const highValueSections = [
    '.xuk-hero',
    '.xuk-brand-proposition',
    '.xuk-flagship',
    '.xuk-popular-products',
    '.xuk-smart-home',
    '.xuk-factory',
    '.xuk-pdp-hero',
    '.xuk-pdp-features',
    '.xuk-pdp-gallery',
    '.xuk-pdp-trust',
    '.xuk-pdp-cta',
  ].join(', ');

  const revealGroups = [
    {
      key: 'brand-heading',
      container: '.xuk-brand-proposition .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.075,
    },
    {
      key: 'brand-chapters',
      container: '.xuk-brand-proposition__grid',
      items: ':scope > .xuk-brand-proposition__item',
      profile: 'scene',
      stagger: 0.1,
    },
    {
      key: 'flagship-heading',
      container: '.xuk-flagship .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.07,
    },
    {
      key: 'flagship-product',
      container: '.xuk-flagship__media-panels',
      profile: 'product',
    },
    {
      key: 'flagship-copy',
      container: '.xuk-flagship__explainer',
      profile: 'text',
    },
    {
      key: 'products-heading',
      container: '.xuk-popular-products .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.07,
    },
    {
      key: 'products-grid',
      container: '.xuk-popular-products__grid',
      items: ':scope > .xuk-popular-products__item',
      profile: 'product',
      stagger: 0.08,
    },
    {
      key: 'factory-heading',
      container: '.xuk-factory .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.07,
    },
    {
      key: 'factory-scene',
      container: '.xuk-factory__media-grid',
      profile: 'scene',
    },
    {
      key: 'factory-proof',
      container: '.xuk-factory__capabilities',
      items: ':scope > .xuk-factory__capability',
      profile: 'data',
      stagger: 0.075,
    },
    {
      key: 'ownership-proof',
      container: '.xuk-ownership__journey',
      items: ':scope > .xuk-ownership__step',
      profile: 'data',
      stagger: 0.065,
    },
    {
      key: 'wholesale-proof',
      container: '.xuk-wholesale__process, .xuk-wholesale__proofs',
      items: ':scope > *',
      profile: 'data',
      stagger: 0.065,
    },
    {
      key: 'pdp-features-heading',
      container: '.xuk-pdp-features .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.06,
    },
    {
      key: 'pdp-features',
      container: '.xuk-pdp-features__grid',
      items: ':scope > .xuk-pdp-features__item',
      profile: 'data',
      stagger: 0.065,
    },
    {
      key: 'pdp-gallery-heading',
      container: '.xuk-pdp-gallery .xuk-section-heading',
      items: ':scope > *',
      profile: 'text',
      stagger: 0.06,
    },
    {
      key: 'pdp-gallery',
      container: '.xuk-pdp-gallery__grid',
      items: ':scope > .xuk-pdp-gallery__item',
      profile: 'scene',
      stagger: 0.065,
    },
    {
      key: 'pdp-trust',
      container: '.xuk-pdp-trust__grid',
      items: ':scope > .xuk-pdp-trust__card',
      profile: 'data',
      stagger: 0.065,
    },
    {
      key: 'quiet-reveal',
      container: 'xuk-reveal',
      profile: 'quiet',
      skipWithin: highValueSections,
    },
  ];

  const stateRules = [
    {
      root: '.xuk-hero__story',
      media: '.xuk-hero__stage:not([hidden])',
    },
    {
      root: '.xuk-flagship__story',
      media: '.xuk-flagship__media-panel:not([hidden]) .xuk-flagship__product-media',
      copy: '.xuk-flagship__copy-panel:not([hidden]) > *',
    },
    {
      root: '.xuk-app-connectivity__tabs',
      media: '.xuk-app-connectivity__state:not([hidden]) .xuk-app-connectivity__media',
      copy: '.xuk-app-connectivity__state:not([hidden]) .xuk-app-connectivity__event, .xuk-app-connectivity__state:not([hidden]) .xuk-app-connectivity__product',
    },
    {
      root: '.xuk-pdp-capability__tabs',
      media: '.xuk-pdp-capability__panel:not([hidden]) .xuk-pdp-capability__media',
      copy: '.xuk-pdp-capability__panel:not([hidden]) .xuk-pdp-capability__panel-copy > *',
    },
  ];

  const profiles = {
    text: { alpha: 0, y: 28, scale: 1, duration: 0.88, ease: 'power3.out' },
    product: { alpha: 0.18, y: 34, scale: 0.965, duration: 1.05, ease: 'power3.out' },
    scene: { alpha: 0.42, y: 16, scale: 1.02, duration: 1.18, ease: 'power2.out' },
    data: { alpha: 0, y: 16, scale: 1, duration: 0.72, ease: 'power2.out' },
    quiet: { alpha: 0, y: 12, scale: 1, duration: 0.58, ease: 'power2.out' },
  };

  function queryAll(scope, selector) {
    const matches = Array.from(scope.querySelectorAll(selector));
    if (scope instanceof Element && scope.matches(selector)) matches.unshift(scope);
    return matches;
  }

  function responsiveProfile(name, viewport) {
    const profile = profiles[name] || profiles.quiet;
    const distanceCap = viewport.desktop ? profile.y : viewport.tablet ? 18 : 10;
    const scale = viewport.desktop
      ? profile.scale
      : profile.scale < 1
        ? Math.max(profile.scale, viewport.tablet ? 0.98 : 0.99)
        : Math.min(profile.scale, viewport.tablet ? 1.012 : 1.006);

    return {
      ...profile,
      y: Math.min(profile.y, distanceCap),
      scale,
      duration: viewport.desktop ? profile.duration : viewport.tablet ? profile.duration * 0.78 : profile.duration * 0.58,
    };
  }

  function mount(scope) {
    if (mounts.has(scope)) mounts.get(scope).revert();

    const media = gsap.matchMedia();
    mounts.set(scope, media);

    media.add(
      {
        desktop: '(min-width: 990px)',
        tablet: '(min-width: 750px) and (max-width: 989px)',
        mobile: '(max-width: 749px)',
        reduceMotion: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        const viewport = context.conditions;
        const registrations = [];
        const mutationObservers = [];
        const revealQueue = new Map();
        let revealObserver;

        if (viewport.reduceMotion) return undefined;

        const queuePlay = (trigger, play) => {
          const plays = revealQueue.get(trigger) || [];
          plays.push(play);
          revealQueue.set(trigger, plays);
        };

        const registerReveal = (container, targets, group) => {
          if (!targets.length || container.dataset.xukMotionBound) return;

          const profile = responsiveProfile(group.profile, viewport);
          const stagger = viewport.desktop
            ? group.stagger || 0
            : viewport.tablet
              ? Math.min(group.stagger || 0, 0.045)
              : Math.min(group.stagger || 0, 0.025);

          container.dataset.xukMotionBound = group.key;
          registrations.push({ container, targets });

          gsap.set(targets, {
            autoAlpha: profile.alpha,
            y: profile.y,
            scale: profile.scale,
            transformOrigin: '50% 50%',
            willChange: 'transform, opacity',
          });

          queuePlay(container, () => {
            gsap.to(targets, {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              duration: profile.duration,
              ease: profile.ease,
              stagger,
              overwrite: 'auto',
              clearProps: 'transform,opacity,visibility,willChange,transformOrigin',
            });
          });
        };

        bindPageEntrances(scope, viewport, registrations);

        revealGroups.forEach((group) => {
          queryAll(scope, group.container).forEach((container) => {
            if (group.skipWithin && container.closest(group.skipWithin)) return;
            const targets = group.items
              ? Array.from(container.querySelectorAll(group.items))
              : [container];
            registerReveal(container, targets, group);
          });
        });

        bindSmartHome(scope, viewport, registrations, queuePlay);
        bindStateTransitions(scope, viewport, mutationObservers);

        if ('IntersectionObserver' in window) {
          revealObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const plays = revealQueue.get(entry.target) || [];
                plays.forEach((play) => play());
                revealQueue.delete(entry.target);
                revealObserver.unobserve(entry.target);
              });
            },
            {
              rootMargin: viewport.mobile ? '0px 0px -4% 0px' : '0px 0px -9% 0px',
              threshold: viewport.desktop ? 0.16 : 0.08,
            }
          );

          revealQueue.forEach((plays, trigger) => revealObserver.observe(trigger));
        } else {
          revealQueue.forEach((plays) => plays.forEach((play) => play()));
          revealQueue.clear();
        }

        return () => {
          revealObserver?.disconnect();
          mutationObservers.forEach((observer) => observer.disconnect());
          registrations.forEach(({ container, targets }) => {
            gsap.killTweensOf(targets);
            gsap.set(targets, {
              clearProps: 'transform,opacity,visibility,willChange,transformOrigin',
            });
            delete container.dataset.xukMotionBound;
            delete container.dataset.xukMotionHero;
            delete container.dataset.xukMotionSmart;
          });
        };
      }
    );
  }

  function bindPageEntrances(scope, viewport, registrations) {
    queryAll(scope, '.xuk-hero').forEach((hero) => {
      if (hero.dataset.xukMotionHero) return;

      const primaryCopy = Array.from(hero.querySelectorAll('.xuk-hero__content > .xuk-hero__heading'));
      const supportingCopy = Array.from(hero.querySelectorAll(
        '.xuk-hero__content > .xuk-eyebrow, .xuk-hero__content > .xuk-hero__description, .xuk-hero__content > .xuk-hero__actions'
      ));
      const product = Array.from(hero.querySelectorAll('.xuk-hero__product-media'));
      const context = Array.from(hero.querySelectorAll('.xuk-hero__product-copy, .xuk-hero__stage-panels'));
      const allTargets = [...primaryCopy, ...supportingCopy, ...product, ...context];
      if (!allTargets.length) return;

      hero.dataset.xukMotionHero = 'true';
      registrations.push({ container: hero, targets: allTargets });

      const distance = viewport.desktop ? 24 : viewport.tablet ? 16 : 9;
      const duration = viewport.desktop ? 0.86 : viewport.tablet ? 0.68 : 0.5;
      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

      timeline
        .fromTo(
          product,
          { y: distance, scale: viewport.desktop ? 0.975 : 0.99 },
          { y: 0, scale: 1, duration: duration * 1.2, clearProps: 'transform' },
          0
        )
        .fromTo(
          primaryCopy,
          { y: distance },
          { y: 0, duration, clearProps: 'transform' },
          0.04
        )
        .fromTo(
          supportingCopy,
          { autoAlpha: 0.72, y: distance },
          { autoAlpha: 1, y: 0, duration, stagger: viewport.desktop ? 0.075 : 0.035, clearProps: 'transform,opacity,visibility' },
          0.08
        )
        .fromTo(
          context,
          { autoAlpha: 0.76, y: Math.max(6, distance * 0.55) },
          { autoAlpha: 1, y: 0, duration: duration * 0.88, clearProps: 'transform,opacity,visibility' },
          viewport.desktop ? 0.24 : 0.12
        );
    });

    queryAll(scope, '.xuk-pdp-hero').forEach((hero) => {
      if (hero.dataset.xukMotionHero) return;

      const primaryCopy = Array.from(hero.querySelectorAll('.xuk-pdp-hero__copy > .xuk-pdp-hero__heading'));
      const supportingCopy = Array.from(hero.querySelectorAll(
        '.xuk-pdp-hero__copy > .xuk-eyebrow, .xuk-pdp-hero__copy > .xuk-pdp-hero__description'
      ));
      const mediaTargets = Array.from(hero.querySelectorAll('.xuk-pdp-hero__media'));
      const allTargets = [...primaryCopy, ...supportingCopy, ...mediaTargets];
      if (!allTargets.length) return;

      hero.dataset.xukMotionHero = 'true';
      registrations.push({ container: hero, targets: allTargets });

      const distance = viewport.desktop ? 26 : viewport.tablet ? 16 : 8;
      const duration = viewport.desktop ? 0.92 : viewport.tablet ? 0.7 : 0.5;
      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

      timeline
        .fromTo(
          mediaTargets,
          { y: distance, scale: viewport.desktop ? 0.968 : 0.99 },
          { y: 0, scale: 1, duration: duration * 1.18, clearProps: 'transform' },
          0
        )
        .fromTo(
          primaryCopy,
          { y: distance },
          { y: 0, duration, clearProps: 'transform' },
          0.06
        )
        .fromTo(
          supportingCopy,
          { autoAlpha: 0.74, y: distance },
          { autoAlpha: 1, y: 0, duration, stagger: viewport.desktop ? 0.07 : 0.03, clearProps: 'transform,opacity,visibility' },
          0.1
        );
    });
  }

  function bindSmartHome(scope, viewport, registrations, queuePlay) {
    queryAll(scope, '.xuk-safety-showroom').forEach((showroom) => {
      if (showroom.dataset.xukMotionSmart) return;

      const stage = showroom.querySelector('.xuk-safety-showroom__stage');
      const hotspots = Array.from(showroom.querySelectorAll('.xuk-safety-showroom__hotspot'));
      const support = Array.from(showroom.querySelectorAll(
        '.xuk-safety-showroom__legend, .xuk-safety-showroom__prompt'
      ));
      const targets = [stage, ...hotspots, ...support].filter(Boolean);
      if (!stage || !targets.length) return;

      showroom.dataset.xukMotionSmart = 'true';
      registrations.push({ container: showroom, targets });

      gsap.set(stage, {
        autoAlpha: viewport.mobile ? 0.72 : 0.55,
        scale: viewport.desktop ? 1.018 : 1.008,
        transformOrigin: '50% 50%',
        willChange: 'transform, opacity',
      });
      gsap.set(hotspots, {
        autoAlpha: 0.18,
        scale: viewport.mobile ? 0.94 : 0.88,
        transformOrigin: '50% 50%',
        willChange: 'transform, opacity',
      });
      if (support.length) gsap.set(support, { autoAlpha: 0, y: viewport.mobile ? 8 : 12 });

      queuePlay(showroom, () => {
        const timeline = gsap.timeline({ defaults: { ease: 'power2.out' } });
        timeline
          .to(stage, {
            autoAlpha: 1,
            scale: 1,
            duration: viewport.desktop ? 1.22 : viewport.tablet ? 0.88 : 0.62,
            clearProps: 'transform,opacity,visibility,willChange,transformOrigin',
          }, 0)
          .to(hotspots, {
            autoAlpha: 1,
            scale: 1,
            duration: viewport.desktop ? 0.72 : 0.48,
            stagger: viewport.desktop ? 0.1 : viewport.tablet ? 0.055 : 0.035,
            clearProps: 'transform,opacity,visibility,willChange,transformOrigin',
          }, viewport.desktop ? 0.34 : 0.18);

        if (support.length) {
          timeline.to(support, {
            autoAlpha: 1,
            y: 0,
            duration: viewport.desktop ? 0.58 : 0.42,
            stagger: viewport.desktop ? 0.07 : 0.035,
            clearProps: 'transform,opacity,visibility',
          }, viewport.desktop ? 0.58 : 0.3);
        }
      });
    });
  }

  function bindStateTransitions(scope, viewport, mutationObservers) {
    stateRules.forEach((rule) => {
      queryAll(scope, rule.root).forEach((root) => {
        let activeTab = root.dataset.activeTab || '';

        const observer = new MutationObserver(() => {
          const nextTab = root.dataset.activeTab || '';
          if (!nextTab || nextTab === activeTab) return;
          if (!activeTab) {
            activeTab = nextTab;
            return;
          }
          activeTab = nextTab;

          const mediaTargets = rule.media ? Array.from(root.querySelectorAll(rule.media)) : [];
          const copyTargets = rule.copy ? Array.from(root.querySelectorAll(rule.copy)) : [];
          if (!mediaTargets.length && !copyTargets.length) return;

          gsap.killTweensOf([...mediaTargets, ...copyTargets]);
          const timeline = gsap.timeline({ defaults: { ease: 'power3.out', overwrite: 'auto' } });
          const distance = viewport.desktop ? 12 : 6;

          if (mediaTargets.length) {
            timeline.fromTo(
              mediaTargets,
              { autoAlpha: 0.55, y: distance, scale: viewport.desktop ? 0.985 : 0.995 },
              {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: viewport.desktop ? 0.62 : 0.4,
                clearProps: 'transform,opacity,visibility',
              },
              0
            );
          }

          if (copyTargets.length) {
            timeline.fromTo(
              copyTargets,
              { autoAlpha: 0.2, y: distance },
              {
                autoAlpha: 1,
                y: 0,
                duration: viewport.desktop ? 0.5 : 0.34,
                stagger: viewport.desktop ? 0.045 : 0.02,
                clearProps: 'transform,opacity,visibility',
              },
              mediaTargets.length ? 0.08 : 0
            );
          }
        });

        observer.observe(root, { attributes: true, attributeFilter: ['data-active-tab'] });
        mutationObservers.push(observer);
      });
    });
  }

  function unmount(scope) {
    mounts.get(scope)?.revert();
    mounts.delete(scope);
  }

  const start = () => {
    mount(document);
    document.documentElement.dataset.xukMotion = 'ready';
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
